<template>
  <div class="legal-page">
    <Navigation />
    <div class="container">
      <h1>Privacy Policy</h1>
      <p class="last-updated">Last updated: January 2026</p>

      <section>
        <h2>1. Privacy Commitment and Scope</h2>
        <p>
          Renants ("we," "us," "our," or "Platform") respects your privacy and is committed to protecting your personal 
          information. This Privacy Policy explains how Renants collects, uses, shares, and protects information about you 
          when you access and use the Renants platform and services. By using Renants, you automatically accept and agree 
          to this Privacy Policy.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        <p>
          Renants may collect the following categories of information:
        </p>
        <ul>
          <li><strong>Personal Information:</strong> Name, email address, phone number, physical address, date of birth, gender</li>
          <li><strong>Account Information:</strong> Username, password, profile details, biographical information, preferences</li>
          <li><strong>Payment Information:</strong> Credit card, debit card, banking details, billing address, transaction history</li>
          <li><strong>Listing and Ad Information:</strong> Classified ads, listings, posts, photos, descriptions, pricing, categories</li>
          <li><strong>Social Interactions:</strong> Connections, messages, comments, likes, shares, follows, and social activity</li>
          <li><strong>Transactional Information:</strong> Service requests, bookings, purchases, inquiries, ratings, reviews</li>
          <li><strong>Device Information:</strong> IP address, browser type, device ID, operating system, mobile device information</li>
          <li><strong>Usage Activity:</strong> How you interact with the platform, pages visited, features used, time spent, clicks</li>
          <li><strong>Location Information:</strong> Geographic location data when permitted by your device</li>
          <li><strong>Communications:</strong> Messages, emails, feedback, support inquiries, and customer service interactions</li>
          <li><strong>Social Media Data:</strong> Information from third-party accounts you link to Renants</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Collect Information</h2>
        <p>
          Renants collects information through:
        </p>
        <ul>
          <li>Direct submission through registration, account creation, and forms</li>
          <li>Cookies and similar tracking technologies that store user preferences and activity</li>
          <li>Web beacons and scripts embedded in our platform to monitor activity</li>
          <li>Analytic tools (such as Google Analytics and Firebase) to track platform traffic and user behavior</li>
          <li>Payment processors when you conduct transactions</li>
          <li>Third-party services you connect to your Renants account</li>
          <li>Communications you send to us directly through email or support channels</li>
          <li>Information about your referrals and other users you invite</li>
        </ul>
      </section>

      <section>
        <h2>4. How We Use Your Information</h2>
        <p>
          Renants uses your information to:
        </p>
        <ul>
          <li>Process, fulfill, and track your requests, orders, and transactions</li>
          <li>Enable posting, browsing, and management of classified ads and listings</li>
          <li>Facilitate communication between users through messaging, comments, and connections</li>
          <li>Display your public profile, listings, and ads to other users</li>
          <li>Facilitate and maintain your access to and use of the platform</li>
          <li>Create and manage your user account and profile</li>
          <li>Provide customer service and technical support</li>
          <li>Send administrative, transactional, and promotional communications</li>
          <li>Improve, enhance, and personalize your platform experience</li>
          <li>Analyze traffic, user behavior, and platform usage patterns</li>
          <li>Conduct research and develop new features and services</li>
          <li>Monitor and prevent fraud, abuse, and security threats</li>
          <li>Protect and enforce Renants' rights, property, and terms</li>
          <li>Comply with legal obligations and respond to government requests</li>
          <li>Create aggregated, de-identified data for business analytics</li>
        </ul>
      </section>

      <section>
        <h2>5. Information Sharing and Disclosure</h2>
        <p>
          Renants may share your information with:
        </p>
        <ul>
          <li>Service providers who assist in operating, maintaining, and improving the platform</li>
          <li>Payment processors and financial institutions for transaction processing</li>
          <li>Other users who view your public profile, listings, ads, and social activity</li>
          <li>Users you communicate with directly through messages or responses to ads</li>
          <li>Renants' employees, agents, advisors, and authorized business partners</li>
          <li>Law enforcement or government agencies in response to legal requirements</li>
          <li>Third parties in connection with mergers, acquisitions, or asset sales involving Renants</li>
          <li>Other parties with your explicit written consent or at your direction</li>
        </ul>
        <p>
          Renants does not sell your personal information to third parties for marketing purposes without your consent.
        </p>
      </section>

      <section>
        <h2>6. Data Retention</h2>
        <p>
          Renants retains your personal and non-personal information for as long as necessary to provide services, maintain 
          your account, and comply with legal obligations. Renants may dispose of or delete any information at any time unless 
          required by law to retain it. You may request deletion of your data subject to certain exceptions and legal requirements.
        </p>
      </section>

      <section>
        <h2>7. Data Security and Protection</h2>
        <p>
          Renants implements industry-standard security measures, including encryption, secure storage, and access controls 
          to help protect your personal information. However, perfect security does not exist on the internet. Your use of 
          Renants is at your own risk. You are responsible for maintaining the confidentiality of your account credentials 
          and should notify Renants immediately of any suspected unauthorized access.
        </p>
      </section>

      <section>
        <h2>8. Public Information and Visibility</h2>
        <p>
          <strong>Important Notice:</strong> When you post classified ads, listings, or engage in social activities on Renants, 
          certain information becomes publicly visible to all users and may be indexed by search engines. This includes:
        </p>
        <ul>
          <li>Your username and public profile information</li>
          <li>All classified ads, listings, and posts you create</li>
          <li>Photos, descriptions, and details in your advertisements</li>
          <li>Public comments, reviews, and social interactions</li>
          <li>Ratings and feedback you receive from other users</li>
          <li>Your social connections and activity (if set to public)</li>
        </ul>
        <p>
          You can control your privacy settings and choose what information to make public, but be aware that posted ads 
          and listings are intended to be publicly visible to facilitate connections between users. Exercise caution when 
          sharing personal information in ads or public posts.
        </p>
      </section>

      <section>
        <h2>9. Your Privacy Rights</h2>
        <p>
          You have the following rights regarding your personal information:
        </p>
        <ul>
          <li>Right to request access to your personal information</li>
          <li>Right to request correction or update of inaccurate information</li>
          <li>Right to request deletion of your personal information</li>
          <li>Right to opt-out of marketing and promotional communications</li>
          <li>Right to request information about how your data is shared</li>
          <li>Right to withdraw consent at any time</li>
        </ul>
        <p>
          To exercise these rights, contact us at <strong>support@renants.com</strong> with your request details.
        </p>
      </section>
    </div>
    <Footer />
  </div>
</template>

<script>
import Navigation from './Navigation.vue';
import Footer from './footer.vue';

export default {
  name: 'PrivacyPolicy',
  components: {
    Navigation,
    Footer
  }
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 32px;
  flex: 1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  color: #333333;
  line-height: 1.8;
}

h1 {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.last-updated {
  font-size: 14px;
  color: #333333;
  margin-bottom: 40px;
  font-style: italic;
}

section {
  margin-bottom: 40px;
}

h2 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #2c2c2c;
  margin-top: 32px;
}

p {
  font-size: 16px;
  margin-bottom: 16px;
  text-align: justify;
}

ul {
  margin-left: 32px;
  margin-bottom: 16px;
}

li {
  font-size: 16px;
  margin-bottom: 12px;
  text-align: justify;
}

a {
  color: #3467d5;
  text-decoration: none;
  transition: color 0.3s ease;
}

a:hover {
  color: #2651b1;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .container {
    padding: 40px 24px;
  }

  h1 {
    font-size: 32px;
  }

  h2 {
    font-size: 20px;
  }

  p,
  li {
    font-size: 15px;
    text-align: left;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 24px 16px;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 18px;
  }

  p,
  li {
    font-size: 14px;
  }
}
</style>
